import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronLeft, Loader2, Target, Check } from "lucide-react";
import { plan } from "../lib/api";

export default function Plan() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const title = params.get("title") || "A chosen future";
  const summary = params.get("summary") || "";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checked, setChecked] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const d = await plan(id, title, summary);
        setData(d);
      } finally {
        setLoading(false);
      }
    })();
  }, [id, title, summary]);

  return (
    <div className="min-h-screen px-5 md:px-8 py-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <button onClick={() => navigate(`/journey/${id}`)} className="btn-pill text-xs" data-testid="plan-back-btn">
          <ChevronLeft size={14} /> Journey
        </button>
        <div className="font-mono-label text-white/50">Future → Plan</div>
        <div style={{ width: 60 }} />
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center mb-10"
      >
        <div className="font-mono-label text-amber-300/80 mb-3 flex items-center justify-center gap-2">
          <Target size={14} /> Turning the future into a plan
        </div>
        <h1 className="text-3xl md:text-4xl font-display font-light leading-tight mb-3">
          {loading ? "Working backwards from your future…" : (data?.headline || `Your path to: ${title}`)}
        </h1>
        <p className="text-sm text-white/50 max-w-xl mx-auto">{summary}</p>
      </motion.div>

      {loading && (
        <div className="text-center py-10 text-white/60">
          <Loader2 className="mx-auto mb-3 animate-spin" />
          Reverse-engineering your future…
        </div>
      )}

      {!loading && data && (
        <div className="space-y-5">
          {data.horizons?.map((h, i) => {
            const colors = ["#00F0FF", "#B026FF", "#FFD700", "#00F0FF"];
            const c = colors[i % colors.length];
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.1 }}
                className="glass rounded-2xl p-6 md:p-8"
                style={{ borderColor: c + "55", boxShadow: `0 0 24px ${c}22` }}
                data-testid={`horizon-${i}`}
              >
                <div className="flex items-center gap-3 mb-4">
                  <div
                    className="w-10 h-10 rounded-full flex items-center justify-center font-display font-semibold"
                    style={{ background: c + "22", border: `1px solid ${c}aa`, color: c, boxShadow: `0 0 16px ${c}55` }}
                  >
                    {i + 1}
                  </div>
                  <div>
                    <div className="font-mono-label text-white/50">Horizon</div>
                    <div className="text-xl font-display" style={{ color: c, textShadow: `0 0 10px ${c}66` }}>
                      {h.label}
                    </div>
                  </div>
                </div>
                <ul className="space-y-3">
                  {h.steps?.map((s, k) => {
                    const key = `${i}-${k}`;
                    const isChecked = checked[key];
                    return (
                      <li key={k}>
                        <button
                          onClick={() => setChecked({ ...checked, [key]: !isChecked })}
                          data-testid={`step-${i}-${k}`}
                          className="w-full text-left flex gap-3 items-start p-3 rounded-xl hover:bg-white/[0.03] transition-colors"
                        >
                          <span
                            className="mt-0.5 w-5 h-5 rounded-full border flex items-center justify-center shrink-0"
                            style={{
                              borderColor: isChecked ? c : "rgba(255,255,255,0.25)",
                              background: isChecked ? c : "transparent",
                              boxShadow: isChecked ? `0 0 10px ${c}` : "none",
                            }}
                          >
                            {isChecked && <Check size={12} color="#05050A" strokeWidth={3} />}
                          </span>
                          <span
                            className={`text-sm leading-relaxed ${isChecked ? "text-white/40 line-through" : "text-white/85"}`}
                          >
                            {s}
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </motion.div>
            );
          })}
          <div className="text-center pt-6">
            <p className="text-xs text-white/40 mb-4">
              Tap any step to mark it done. This plan lives on your device — nothing is sent.
            </p>
            <button onClick={() => navigate(`/journey/${id}`)} className="btn-pill btn-pill-primary">
              Return to the simulation
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
